// Design agent: Spec 003 — wraps any rendered IPv4 address (inspected address, network, broadcast,
// first/last host) so hovering or focusing it reveals a popover with the address's full binary
// breakdown. This turns every address already on screen into a teaching aid without adding new
// dedicated UI real estate for it.
// Developer agent: Extracted out of IpInspector.js so the interaction logic (open delay, close grace
// delay, placement, keyboard handling) can be unit tested on its own. All bit arithmetic still lives
// in utils/binaryBreakdown.js and all markup in BinaryBreakdown.js — this file owns interaction only.
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import BinaryBreakdown from './BinaryBreakdown';

// Design agent: A short delay before opening on hover keeps a quick mouse pass across the card from
// flashing the popover, while the (longer-feeling in practice) grace delay on the way out lets the
// pointer travel from the address to the popover's copy buttons without the popover vanishing
// mid-journey — the exact problem reported against the first implementation.
// Developer agent: Both are module constants so the test suite can reason about them in one place.
export const HOVER_OPEN_DELAY_MS = 250;
export const HOVER_CLOSE_GRACE_MS = 250;

// Design agent: The popover must sit immediately under the address it explains — a gap of a few
// pixels, not a few hundred — so the eye (and the mouse) travel the shortest possible distance.
// Developer agent: VIEWPORT_MARGIN_PX keeps the panel off the very edge of the window when it has to
// be clamped horizontally or vertically.
const POPOVER_GAP_PX = 8;
const VIEWPORT_MARGIN_PX = 12;

// Design agent: At most one binary breakdown may be on screen at a time. Without this, an address
// left open by the keyboard (it stays open while focused) and a second one opened by the mouse would
// overlap into an unreadable stack of two panels.
// Developer agent: Module-level, deliberately: the coordination is between sibling instances that
// have no common React ancestor to hold the state. Each entry is { token, close }, the token being
// the owning instance's identity so an instance can recognise (and clear) its own registration.
let currentlyOpenPopover = null;

// Design agent: Places the popover directly below the hovered address, flips it above when there is
// not enough room below, and always keeps it fully inside the viewport horizontally so the copy
// buttons can never end up off-screen.
// Developer agent: Pure function of three plain objects (no DOM access) so it is directly testable.
// `popoverSize` may legitimately be {0, 0} on the very first layout pass (or under happy-dom, which
// does not lay out): the maths then degrades to "just below the trigger", which is corrected on the
// next pass once the real size is known.
export function computePopoverPosition(triggerRect, popoverSize, viewport) {
  const popoverWidth = popoverSize.width;
  const popoverHeight = popoverSize.height;

  const spaceBelow = viewport.height - triggerRect.bottom;
  const spaceAbove = triggerRect.top;
  const neededHeight = popoverHeight + POPOVER_GAP_PX + VIEWPORT_MARGIN_PX;

  let placement = 'below';
  let top = triggerRect.bottom + POPOVER_GAP_PX;

  // Design agent: Only flip when the popover genuinely does not fit below AND there is more room
  // above — flipping into an even tighter space would make things worse, not better.
  if (popoverHeight > 0 && spaceBelow < neededHeight && spaceAbove > spaceBelow) {
    placement = 'above';
    top = triggerRect.top - POPOVER_GAP_PX - popoverHeight;
  }

  const lowestAllowedTop = Math.max(
    VIEWPORT_MARGIN_PX,
    viewport.height - VIEWPORT_MARGIN_PX - popoverHeight,
  );
  top = Math.min(Math.max(top, VIEWPORT_MARGIN_PX), lowestAllowedTop);

  const rightmostAllowedLeft = Math.max(
    VIEWPORT_MARGIN_PX,
    viewport.width - VIEWPORT_MARGIN_PX - popoverWidth,
  );
  const left = Math.min(Math.max(triggerRect.left, VIEWPORT_MARGIN_PX), rightmostAllowedLeft);

  return { top, left, placement };
}

function HoverableAddress({ id, address, prefix, networkColor, hostColor, label, children }) {
  const [isPopoverVisible, setIsPopoverVisible] = useState(false);
  const [popoverPosition, setPopoverPosition] = useState(null);
  const triggerRef = useRef(null);
  const popoverRef = useRef(null);
  const openTimerRef = useRef(null);
  const closeTimerRef = useRef(null);
  // Design agent: Escape, and tabbing out of the popover, both hand focus back to the address. That
  // focus must NOT re-open the panel the user just dismissed, otherwise Tab would loop forever
  // between the address and the two copy buttons and Escape would look broken.
  // Developer agent: A ref (not state) because it is consumed by the focus handler that runs in the
  // very same commit as the programmatic focus() call.
  const suppressReopenOnFocusRef = useRef(false);
  // Developer agent: Stable identity for this instance, used as the key of the "only one popover
  // open at a time" registry declared at module scope.
  const instanceTokenRef = useRef({});

  // Developer agent: One helper per timer so every entry point cancels exactly what it means to
  // cancel — an open that is no longer wanted, or a close that a returning pointer has cancelled.
  const cancelPendingOpen = useCallback(() => {
    if (openTimerRef.current !== null) {
      clearTimeout(openTimerRef.current);
      openTimerRef.current = null;
    }
  }, []);

  const cancelPendingClose = useCallback(() => {
    if (closeTimerRef.current !== null) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  // Developer agent: The single place that hides the panel, so the module-level registry is released
  // on every close path (Escape, click outside, blur, grace delay, unmount).
  const hidePopover = useCallback(() => {
    if (currentlyOpenPopover !== null && currentlyOpenPopover.token === instanceTokenRef.current) {
      currentlyOpenPopover = null;
    }
    setIsPopoverVisible(false);
    // Design agent: Forget the measured position on close so the next open is measured afresh
    // against wherever the address sits by then (the page may have scrolled or reflowed).
    setPopoverPosition(null);
  }, []);

  const closeImmediately = useCallback(() => {
    cancelPendingOpen();
    cancelPendingClose();
    hidePopover();
  }, [cancelPendingOpen, cancelPendingClose, hidePopover]);

  // Developer agent: The single place that shows the panel — it closes whichever other address had
  // one open before claiming the registry for this instance.
  const showPopover = useCallback(() => {
    if (currentlyOpenPopover !== null && currentlyOpenPopover.token !== instanceTokenRef.current) {
      currentlyOpenPopover.close();
    }
    currentlyOpenPopover = { token: instanceTokenRef.current, close: closeImmediately };
    setIsPopoverVisible(true);
  }, [closeImmediately]);

  const openImmediately = useCallback(() => {
    cancelPendingOpen();
    cancelPendingClose();
    showPopover();
  }, [cancelPendingOpen, cancelPendingClose, showPopover]);

  // Design agent: The pointer entering either the address or the popover means "I am still reading
  // this", so it both cancels a pending close and (from the address) starts the open countdown.
  const handleTriggerMouseEnter = useCallback(() => {
    cancelPendingClose();
    if (isPopoverVisible) {
      return;
    }
    cancelPendingOpen();
    openTimerRef.current = setTimeout(() => {
      openTimerRef.current = null;
      showPopover();
    }, HOVER_OPEN_DELAY_MS);
  }, [cancelPendingClose, cancelPendingOpen, isPopoverVisible, showPopover]);

  // Design agent: Leaving the address does NOT close the popover straight away: the pointer is very
  // often on its way to the popover's own buttons, a few pixels below. The grace delay gives it time
  // to arrive; entering the popover cancels the close.
  const handlePointerLeave = useCallback(() => {
    cancelPendingOpen();
    cancelPendingClose();
    closeTimerRef.current = setTimeout(() => {
      closeTimerRef.current = null;
      hidePopover();
    }, HOVER_CLOSE_GRACE_MS);
  }, [cancelPendingOpen, cancelPendingClose, hidePopover]);

  const handlePopoverMouseEnter = useCallback(() => {
    cancelPendingClose();
  }, [cancelPendingClose]);

  // Design agent: Keyboard users should see the popover the instant they tab onto the address, with
  // no artificial delay — the delay only exists to filter out incidental mouse passes.
  const handleTriggerFocus = useCallback(() => {
    if (suppressReopenOnFocusRef.current) {
      suppressReopenOnFocusRef.current = false;
      return;
    }
    openImmediately();
  }, [openImmediately]);

  // Developer agent: Single exit path for "dismiss and give the address its focus ring back", used by
  // Escape inside the popover and by Tab leaving either end of it.
  const dismissAndReturnFocusToTrigger = useCallback(() => {
    closeImmediately();
    if (triggerRef.current) {
      suppressReopenOnFocusRef.current = true;
      triggerRef.current.focus();
    }
  }, [closeImmediately]);

  // Developer agent: The popover lives in a portal on <body>, so "focus moved inside" can no longer
  // be answered with currentTarget.contains(): both the trigger node and the popover node have to be
  // consulted explicitly.
  const isInsideTriggerOrPopover = useCallback((node) => {
    if (!node) {
      return false;
    }
    if (triggerRef.current && triggerRef.current.contains(node)) {
      return true;
    }
    return Boolean(popoverRef.current && popoverRef.current.contains(node));
  }, []);

  const handleFocusLeave = useCallback(
    (event) => {
      if (isInsideTriggerOrPopover(event.relatedTarget)) {
        return;
      }
      closeImmediately();
    },
    [isInsideTriggerOrPopover, closeImmediately],
  );

  // Design agent: With the popover portalled to <body> it is no longer the trigger's DOM neighbour,
  // so Tab would skip straight past the copy buttons. These two handlers restore the expected
  // reading order: address -> Copy binary -> Copy dotted decimal -> back to the address (popover
  // closed), from where a further Tab continues into the rest of the card.
  // Developer agent: Escape is handled here too, so the trigger dismisses the popover even when the
  // document-level listener is not yet attached (i.e. on the very keystroke that opens it).
  const handleTriggerKeyDown = useCallback(
    (event) => {
      // Developer agent: React bubbles events from a portal along the REACT tree, so a keystroke
      // handled inside the popover also reaches this handler. Anything that did not originate on the
      // address itself has already been dealt with by handlePopoverKeyDown.
      if (event.target !== event.currentTarget) {
        return;
      }
      if (event.key === 'Escape') {
        closeImmediately();
        return;
      }
      if (event.key !== 'Tab' || event.shiftKey || !isPopoverVisible) {
        return;
      }
      const popoverNode = popoverRef.current;
      if (!popoverNode) {
        return;
      }
      const firstButton = popoverNode.querySelector('button');
      if (firstButton) {
        event.preventDefault();
        firstButton.focus();
      }
    },
    [closeImmediately, isPopoverVisible],
  );

  const handlePopoverKeyDown = useCallback(
    (event) => {
      if (event.key === 'Escape') {
        dismissAndReturnFocusToTrigger();
        return;
      }
      if (event.key !== 'Tab') {
        return;
      }
      const popoverNode = popoverRef.current;
      if (!popoverNode) {
        return;
      }
      const buttons = Array.from(popoverNode.querySelectorAll('button'));
      const focusedIndex = buttons.indexOf(document.activeElement);
      const isLeavingForward = !event.shiftKey && focusedIndex === buttons.length - 1;
      const isLeavingBackward = event.shiftKey && focusedIndex === 0;
      if (!isLeavingForward && !isLeavingBackward) {
        return;
      }
      // Design agent: Tabbing out of either end hands focus back to the address and dismisses the
      // popover, so the next Tab simply continues through the card instead of looping.
      event.preventDefault();
      dismissAndReturnFocusToTrigger();
    },
    [dismissAndReturnFocusToTrigger],
  );

  // Design agent: Measures the address's real on-screen box and pins the popover a few pixels under
  // it. Recomputed on scroll and resize so the panel follows the address instead of drifting away
  // from it.
  // Developer agent: useLayoutEffect (not useEffect) so the position is applied in the same frame the
  // popover first paints, avoiding a visible jump from the default top/left of 0.
  useLayoutEffect(() => {
    if (!isPopoverVisible) {
      return undefined;
    }

    const updatePosition = () => {
      const triggerNode = triggerRef.current;
      const popoverNode = popoverRef.current;
      if (!triggerNode || !popoverNode) {
        return;
      }
      const triggerRect = triggerNode.getBoundingClientRect();
      // Developer agent: offsetWidth/offsetHeight rather than getBoundingClientRect() because the
      // popover fades in and any transform on it would distort a measured rect.
      const popoverSize = { width: popoverNode.offsetWidth, height: popoverNode.offsetHeight };
      const viewport = { width: window.innerWidth, height: window.innerHeight };
      setPopoverPosition(computePopoverPosition(triggerRect, popoverSize, viewport));
    };

    updatePosition();
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [isPopoverVisible]);

  // Design agent: Escape anywhere and a click outside both dismiss the popover, which is what a user
  // expects from any transient panel — and what spec 003 asks for.
  // Developer agent: mousedown (not click) so the popover disappears as soon as the user commits to
  // clicking elsewhere. Any mouse button counts here: a right-click outside is still "the user has
  // moved on". Clicks landing on the trigger or inside the popover are ignored.
  useEffect(() => {
    if (!isPopoverVisible) {
      return undefined;
    }

    const handleDocumentKeyDown = (event) => {
      if (event.key === 'Escape') {
        closeImmediately();
      }
    };

    const handleDocumentMouseDown = (event) => {
      if (isInsideTriggerOrPopover(event.target)) {
        return;
      }
      closeImmediately();
    };

    document.addEventListener('keydown', handleDocumentKeyDown);
    document.addEventListener('mousedown', handleDocumentMouseDown);
    return () => {
      document.removeEventListener('keydown', handleDocumentKeyDown);
      document.removeEventListener('mousedown', handleDocumentMouseDown);
    };
  }, [isPopoverVisible, closeImmediately, isInsideTriggerOrPopover]);

  // Developer agent: Clears both pending timers on unmount so a delayed setState never fires against
  // an unmounted component (e.g. the entry is deleted while a timer is in flight).
  useEffect(() => {
    // Developer agent: The token is read into a local on mount, as the exhaustive-deps rule requires
    // for a ref consumed by a cleanup function. It is created once per instance and never reassigned,
    // so the local and the ref always hold the same object.
    const instanceToken = instanceTokenRef.current;
    return () => {
      if (openTimerRef.current !== null) {
        clearTimeout(openTimerRef.current);
      }
      if (closeTimerRef.current !== null) {
        clearTimeout(closeTimerRef.current);
      }
      // Developer agent: A component removed while its popover was open (the user deletes the entry)
      // must not leave a stale closer behind in the module-level registry.
      if (currentlyOpenPopover !== null && currentlyOpenPopover.token === instanceToken) {
        currentlyOpenPopover = null;
      }
    };
  }, []);

  const popoverId = `${id}-popover`;
  // Design agent: Until the first measurement lands the panel is kept invisible rather than drawn at
  // the top-left corner of the window, so the user never sees it jump into place.
  const popoverStyle = popoverPosition
    ? { top: `${popoverPosition.top}px`, left: `${popoverPosition.left}px` }
    : { top: 0, left: 0, visibility: 'hidden' };

  return (
    <span
      className="hoverable-address"
      id={id}
      ref={triggerRef}
      tabIndex={0}
      role="button"
      aria-haspopup="dialog"
      aria-expanded={isPopoverVisible}
      aria-describedby={isPopoverVisible ? popoverId : undefined}
      // Design agent: Inline styling (rather than a new App.css rule) keeps this affordance's
      // footprint limited to the files spec 003 actually touches — a dashed underline and a "help"
      // cursor are enough to hint that the address is interactive.
      style={{ cursor: 'help', borderBottom: '1px dashed var(--md-sys-color-outline)' }}
      onMouseEnter={handleTriggerMouseEnter}
      onMouseLeave={handlePointerLeave}
      onFocus={handleTriggerFocus}
      onBlur={handleFocusLeave}
      onKeyDown={handleTriggerKeyDown}
    >
      {children}
      {isPopoverVisible &&
        typeof document !== 'undefined' &&
        createPortal(
          <BinaryBreakdown
            id={popoverId}
            address={address}
            prefix={prefix}
            networkColor={networkColor}
            hostColor={hostColor}
            label={label}
            containerRef={popoverRef}
            style={popoverStyle}
            onMouseEnter={handlePopoverMouseEnter}
            onMouseLeave={handlePointerLeave}
            onKeyDown={handlePopoverKeyDown}
            onBlur={handleFocusLeave}
          />,
          document.body,
        )}
    </span>
  );
}

export default HoverableAddress;
